import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreatePlatformOrganizationPayload } from '../../shared/services/api.service';

interface CreatePlatformOrganizationDialogData {
  displayName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string;
  mailLogoUrl?: string;
  initialAdminEmails?: string;
  billingOwnerEmail?: string;
  billingPaymentType?: 'CARD' | 'INVOICE';
}

@Component({
  selector: 'app-create-platform-organization-dialog',
  templateUrl: './create-platform-organization-dialog.component.html',
  styleUrls: ['./create-platform-organization-dialog.component.css'],
})
export class CreatePlatformOrganizationDialogComponent {
  organizationForm: FormGroup;
  emailListError: string | null = null;
  readonly maxAdminEmails = 20;
  selectedBillingOwnerIndex: number | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<CreatePlatformOrganizationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: CreatePlatformOrganizationDialogData
  ) {
    const initialEmails = this.parseInitialEmails(data?.initialAdminEmails || '');
    const billingOwnerIndex = data?.billingOwnerEmail
      ? initialEmails.findIndex((email) => email.toLowerCase() === String(data.billingOwnerEmail || '').toLowerCase())
      : -1;
    this.selectedBillingOwnerIndex = billingOwnerIndex >= 0 ? billingOwnerIndex : null;
    this.organizationForm = this.fb.group({
      displayName: [data?.displayName || '', [Validators.required, Validators.maxLength(120)]],
      primaryColor: [data?.primaryColor || '#1e88e5', [Validators.required]],
      secondaryColor: [data?.secondaryColor || '#00A3E0', [Validators.required]],
      logoUrl: [data?.logoUrl || '', [Validators.required]],
      mailLogoUrl: [data?.mailLogoUrl || ''],
      billingPaymentType: [data?.billingPaymentType || 'CARD', [Validators.required]],
      adminEmails: this.fb.array(
        (initialEmails.length > 0 ? initialEmails : ['']).map((email) =>
          this.fb.control(email, [Validators.required, Validators.email])
        )
      ),
    });
  }

  get adminEmails(): FormArray<FormControl<string | null>> {
    return this.organizationForm.get('adminEmails') as FormArray<FormControl<string | null>>;
  }

  addAdminEmailField(): void {
    if (this.adminEmails.length >= this.maxAdminEmails) return;
    this.adminEmails.push(this.fb.control('', [Validators.required, Validators.email]));
  }

  removeAdminEmailField(index: number): void {
    if (this.adminEmails.length <= 1) return;
    this.adminEmails.removeAt(index);
    if (this.selectedBillingOwnerIndex === index) {
      this.selectedBillingOwnerIndex = null;
    } else if (this.selectedBillingOwnerIndex !== null && this.selectedBillingOwnerIndex > index) {
      this.selectedBillingOwnerIndex -= 1;
    }
  }

  setBillingOwnerIndex(index: number): void {
    this.selectedBillingOwnerIndex = index;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.organizationForm.invalid) return;
    const raw = this.organizationForm.getRawValue();
    const emails = this.adminEmails.controls
      .map((control) => String(control.value || '').trim().toLowerCase())
      .filter(Boolean);
    if (emails.length === 0) {
      this.emailListError = 'Provide at least one admin email.';
      return;
    }
    const invalidEmail = emails.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmail) {
      this.emailListError = `Invalid email: ${invalidEmail}`;
      return;
    }
    if (
      this.selectedBillingOwnerIndex === null ||
      this.selectedBillingOwnerIndex < 0 ||
      this.selectedBillingOwnerIndex >= emails.length
    ) {
      this.emailListError = 'Select one admin email as billing owner.';
      return;
    }
    this.emailListError = null;
    const billingOwnerEmail = emails[this.selectedBillingOwnerIndex];
    const payload: CreatePlatformOrganizationPayload = {
      displayName: String(raw.displayName || '').trim(),
      primaryColor: String(raw.primaryColor || '').trim(),
      secondaryColor: String(raw.secondaryColor || '').trim(),
      logoUrl: String(raw.logoUrl || '').trim(),
      mailLogoUrl: String(raw.mailLogoUrl || '').trim() || null,
      initialAdminEmails: emails,
      billingOwnerEmail,
      billingPaymentType:
        String(raw.billingPaymentType || '').trim().toUpperCase() === 'INVOICE' ? 'INVOICE' : 'CARD',
    };
    this.dialogRef.close(payload);
  }

  private parseInitialEmails(raw: string): string[] {
    return String(raw || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }
}
